import React from 'react';
import { 
  ArrowLeft,
  Download,
  DeviceMobile,
  CheckCircle,
  Warning
} from '@phosphor-icons/react';
import { 
  Box,
  Typography,
  Container,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert
} from '@mui/material';
import { PageContainer, Content } from '../../PageTemplate';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const GradientHero = styled.div`
  background: linear-gradient(
    150deg,
    #000000 0%,
    #1a1a1a 50%,
    #000000 100%
  );
  background-size: 200% 200%;
  animation: gradientMove 15s ease infinite;
  padding: 6rem 2rem 3rem 2rem;
  margin-top: -80px;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  @keyframes gradientMove {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const BackButton = styled(IconButton)`
  position: absolute;
  left: 24px;
  top: 24px;
  color: white;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  z-index: 10;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const HeroContent = styled(motion.div)`
  position: relative;
  z-index: 2;
  padding-top: 2rem;
`;

const ArticleSection = styled(Paper)`
  padding: 2rem;
  margin-bottom: 2rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(229, 231, 235, 0.5);
`;

const GettingStarted = () => {
  const navigate = useNavigate();

  return (
    <PageContainer>
      <GradientHero>
        <BackButton onClick={() => navigate('/resources/help-center/ios')}>
          <ArrowLeft size={24} />
        </BackButton>
        <HeroContent
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Download size={64} weight="duotone" style={{ marginBottom: '1rem' }} />
          <motion.h1
            style={{
              fontSize: '3.5rem',
              marginBottom: '1.5rem',
              fontWeight: 700,
              position: 'relative',
              zIndex: 2
            }}
          >
            Getting Started with ServTrackr iOS
          </motion.h1>
          <motion.p
            style={{
              fontSize: '1.25rem',
              maxWidth: '800px',
              margin: '0 auto',
              opacity: 0.9,
              lineHeight: 1.6,
              position: 'relative',
              zIndex: 2
            }}
          >
            Learn how to download, install, and set up the ServTrackr iOS app
          </motion.p>
        </HeroContent>
      </GradientHero>

      <Content>
        <Container maxWidth="md">
          <ArticleSection>
            <Typography variant="h4" gutterBottom>
              System Requirements
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <DeviceMobile size={24} weight="duotone" />
                </ListItemIcon>
                <ListItemText 
                  primary="iOS 14.0 or later"
                  secondary="Compatible with iPhone and iPad"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle size={24} weight="duotone" />
                </ListItemIcon>
                <ListItemText 
                  primary="Internet Connection"
                  secondary="Required for real-time updates and synchronization"
                />
              </ListItem>
            </List>
          </ArticleSection>

          <ArticleSection>
            <Typography variant="h4" gutterBottom>
              Installation Steps
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle size={24} weight="duotone" />
                </ListItemIcon>
                <ListItemText 
                  primary="Open the App Store"
                  secondary="Search for 'ServTrackr'"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle size={24} weight="duotone" />
                </ListItemIcon>
                <ListItemText 
                  primary="Download the App"
                  secondary="Tap 'Get' or the download icon"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle size={24} weight="duotone" />
                </ListItemIcon>
                <ListItemText 
                  primary="Launch the App"
                  secondary="Open ServTrackr from your home screen"
                />
              </ListItem>
            </List>
          </ArticleSection>

          <ArticleSection>
            <Typography variant="h4" gutterBottom>
              Initial Setup
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle size={24} weight="duotone" />
                </ListItemIcon>
                <ListItemText 
                  primary="Sign In"
                  secondary="Use your ServTrackr account credentials"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle size={24} weight="duotone" />
                </ListItemIcon>
                <ListItemText 
                  primary="Enable Notifications"
                  secondary="Allow push notifications for important updates"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle size={24} weight="duotone" />
                </ListItemIcon>
                <ListItemText 
                  primary="Sync Data"
                  secondary="Your data will automatically sync with the cloud"
                />
              </ListItem>
            </List>
          </ArticleSection>

          <ArticleSection>
            <Typography variant="h4" gutterBottom>
              Important Notes
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Make sure to keep your app updated to the latest version for the best experience and security.
            </Alert>
            <Alert severity="warning" sx={{ mb: 2 }}>
              If you're using an older iOS version, some features may not be available.
            </Alert>
          </ArticleSection>
        </Container>
      </Content>
    </PageContainer>
  );
};

export default GettingStarted; 